/**
 * Campaign (stream) detail. Delivery is the tab that matters and lives in
 * `CampaignDeliveryTab`; the rest land as their endpoints are converted.
 * Under the routing, the workflows that act on this campaign's leads once
 * routing is done, read only (they are edited on the Workflows page).
 *
 * The header says what arrives (the row type) and what the stream does
 * with it (sells, assigns, sends or stores only), both from the server,
 * then what each row is checked for on arrival (duplicates, suppression,
 * phone checks), linking to where each is edited.
 * Money (prices, caps, supplier cost) shows only on a stream that sells.
 */
import {
  Alert, Anchor, Badge, Breadcrumbs, Card, Group, Loader, Stack, Tabs, Text, Title,
} from '@mantine/core';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCampaignDetail } from '../api/queries';
import { useVocab } from '../vocab';
import { CampaignDeliveryTab } from './CampaignDeliveryTab';
import { CampaignPostingGuideTab, CampaignSupplierGuideTab } from './CampaignPostingGuide';
import { CampaignSettingsTab } from './CampaignTabs';
import { UsedByPanel } from './workflows/UsedByPanel';
import { DoesChips, doesSentence, showsMoney, useRowTypes } from './streams/streamShared';
import { ArrivalChecksCard } from './streams/ArrivalChecks';

export function CampaignDetailPage() {
  const { id = null } = useParams<{ id: string }>();
  const { data, isPending, error } = useCampaignDetail(id);
  const v = useVocab();
  const [tab, setTab] = useState<string>('delivery');
  const rowTypes = useRowTypes();

  return (
    <Stack>
      <Breadcrumbs>
        <Anchor component={Link} to="/campaigns" size="sm">{v.Many('campaign')}</Anchor>
        <Text size="sm" c="dimmed">{data?.name ?? '…'}</Text>
      </Breadcrumbs>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}

      {data && (
        <>
          <Group gap="sm">
            <Title order={2}>{data.name}</Title>
            <Badge variant="light" color={data.active ? 'chartGreen' : 'gray'}>
              {data.active ? 'Active' : 'Inactive'}
            </Badge>
            {data.is_reference_list && (
              <Badge variant="outline" color="gray">reference list</Badge>
            )}
            {!data.is_reference_list && (
              <Badge variant="outline" color="gray" data-testid="row-type">{rowTypes.label(data.row_type)}</Badge>
            )}
          </Group>
          {data.description && <Text size="sm" c="dimmed">{data.description}</Text>}

          {data.does && (
            <Card withBorder padding="sm" data-testid="stream-does">
              <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                <Stack gap={4}>
                  <Text size="sm" fw={600}>{`What this ${v.one('campaign')} does`}</Text>
                  <Text size="sm" c="dimmed">{doesSentence(data.does, v)}</Text>
                </Stack>
                <DoesChips does={data.does} v={v} />
              </Group>
              {!showsMoney(data.does) && (
                <Text size="xs" c="dimmed" mt={6} data-testid="no-money">
                  {`Nothing is sold here, so there are no prices, caps or returns on this ${v.one('campaign')}.`}
                </Text>
              )}
            </Card>
          )}

          {/* What a row meets before routing, summarised from where each
              check is set; each line links to the tab that edits it. A
              reference list holds match values, so it has none to show. */}
          {data.arrival_checks && !data.is_reference_list && (
            <ArrivalChecksCard checks={data.arrival_checks} onOpen={setTab} />
          )}

          <Tabs value={tab} onChange={(t) => t && setTab(t)}>
            {/* Tab names follow the existing app's campaign detail rather than
                inventing new vocabulary for the same things. That app also has
                a Distribution Log tab with no equivalent here; the Delivery tab
                now answers the same question in aggregate, and the per-lead
                view lives in the lead detail modal. */}
            <Tabs.List mb="md">
              <Tabs.Tab value="delivery">Delivery</Tabs.Tab>
              <Tabs.Tab value="settings">Settings</Tabs.Tab>
              {/* Values kept from when these were Fields and API. */}
              <Tabs.Tab value="fields">Posting guide</Tabs.Tab>
              <Tabs.Tab value="api">{`${v.One('supplier')} guide`}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="delivery">
              <Stack>
                <CampaignDeliveryTab campaignId={id ?? undefined} groups={data.groups} money={showsMoney(data.does)} />
                <UsedByPanel
                  refType="campaign"
                  refId={id}
                  title={`${v.Many('workflow')} using this ${v.one('campaign')}`}
                  empty={`Routing above decides who buys. No ${v.many('workflow')} act on this ${v.one('campaign')}'s ${v.many('lead')} afterwards.`}
                  testId="campaign-workflows"
                />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="settings"><CampaignSettingsTab campaign={data} /></Tabs.Panel>
            {/* Mounted only while open: both sample recent leads. */}
            <Tabs.Panel value="fields">
              {tab === 'fields' && (
                <CampaignPostingGuideTab campaign={data} onPreview={() => setTab('api')} />
              )}
            </Tabs.Panel>
            <Tabs.Panel value="api">
              {tab === 'api' && <CampaignSupplierGuideTab campaign={data} />}
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </Stack>
  );
}
